import * as React from 'react'
import * as Redux from 'react-redux'

import { Link as RouterLink, type To } from 'react-router-dom'
import { styled, css } from 'next-yak'

import * as model from '@owl-nest/models'
import { t } from '@owl-nest/localize'
import {
  BREAKPOINTS,
  COLORS,
  illustrations,
  Link,
  type LinkProps,
  Pastille,
  styles as S,
} from '@ulule/owl-kit-components/next'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'

import * as projectModel from '../../../models/project'
import { PATHS } from '../../../paths'
import { FollowProjectButton } from './FollowProjectButton'
import { useNavigation } from '../../../hooks/useNavigation'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../utils/trackerEnums'
import { type RootState } from '../../../../reducers'
import * as modalAction from '../../../../actions/modal'
import { DisplayMode } from '../../../context'
import * as URLs from '../../../utils/URLs'
import { EDIT_MODE_LAPTOP_BREAKPOINT } from '../constants'

export const SidebarPrimaryBlock = styled.article<{ $fullWidth?: boolean }>`
  background-color: ${COLORS.PRIMARY_SAND_100};
  box-sizing: border-box;
  margin-top: 60px;
  max-width: 100%;
  padding: 40px 20px 20px;
  position: relative;
  text-align: center;

  ${({ $fullWidth = false }) => {
    if ($fullWidth) {
      return css`
        width: 100%;
      `
    }
  }}

  ${S.heading.XXXS} {
    margin-bottom: 5px;
  }

  ${S.copy.M.light} {
    margin-bottom: 16px;
  }

  ${Pastille} {
    left: calc(50% - 30px);
    margin: 0 auto 5px;
    position: absolute;
    top: -30px;

    span {
      font-size: 35px;
    }
  }
`

export const SidebarSecondaryBlock = styled.article`
  border: 2px solid ${COLORS.PRIMARY_SAND_100};
  padding: 24px 21px;
  text-align: center;

  ${S.button.CallToAction} {
    margin-top: 12px;
  }
`

const ctaWrapperBreakpointStyle = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CTAWrapper = styled.div<{ $isEditMode: boolean }>`
  display: none;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_LAPTOP_BREAKPOINT} {
          ${ctaWrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${ctaWrapperBreakpointStyle};
        }
      `
    }
  }}
`

const ctaBreakpointStyle = css`
  margin: 0 auto;
  margin-top: 34px;
  margin-bottom: 14px;
  width: 260px;
`

const CTA = styled(S.button.CallToAction)<{ $isEditMode: boolean }>`
  margin-bottom: 20px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_LAPTOP_BREAKPOINT} {
          ${ctaBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${ctaBreakpointStyle};
        }
      `
    }
  }}
`

type SidebarNewsCTAProps = {
  className?: string
  source: TRACKING_SOURCE
}

export function SidebarNewsCTA({ className, source }: SidebarNewsCTAProps): React.ReactElement<unknown> {
  return (
    <SidebarPrimaryBlock className={className}>
      <Pastille noBorder size="small" style={{ backgroundColor: COLORS.PRIMARY_SAND_200 }}>
        <illustrations.twoToned.PaperPlane size={58} />
      </Pastille>
      <S.heading.XXXS>{t('Receive project posts directly in your inbox!')}</S.heading.XXXS>
      <S.copy.M.light>{t('Follow the project to be updated on its progress.')}</S.copy.M.light>
      <FollowProjectButton source={source} shouldReloadPage={true} />
    </SidebarPrimaryBlock>
  )
}

export function SidebarRewardsCTA(): React.ReactElement<unknown> {
  const navigation = useNavigation()
  return (
    <SidebarSecondaryBlock>
      <S.copy.M.light>{t('Back the project and receive rewards in return.')}</S.copy.M.light>
      <S.button.CallToAction as={RouterLink} kind="secondary" to={navigation.pathTo(PATHS.REWARDS)}>
        {t('See rewards')}
      </S.button.CallToAction>
    </SidebarSecondaryBlock>
  )
}

export function SidebarDonationCTA(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const dispatch = Redux.useDispatch()
  const isEditMode = React.useContext(DisplayMode) === 'edit'
  const tracking = useTracking()

  return (
    <SidebarSecondaryBlock>
      <S.copy.M.light>{t('Make a donation to support this project and help it become a success')}</S.copy.M.light>
      {model.project.hasCheckout(project) && (
        <CTAWrapper $isEditMode={isEditMode}>
          <CTA
            $isEditMode={isEditMode}
            onClick={() => {
              if (project.is_online && !model.project.isFinished<projectModel.ExtraFields>(project)) {
                if (!model.project.isOrdersEnabled<projectModel.ExtraFields>(project)) {
                  dispatch(modalAction.toggleModalPaymentDisturbance(true))
                } else {
                  tracking.track({
                    event: EVENT_TYPE.GENERIC,
                    eventCategory: CATEGORY.PROJECT_PAGE,
                    eventAction: TRACKING_ACTION.ACCESS_CHECKOUT,
                    eventLabel: TRACKING_SOURCE.FIRST_TIME_FOOTER,
                  })
                  document.location.href = URLs.getCheckoutURL(project.id)
                }
              } else if (project.post_campaign_link) {
                dispatch(modalAction.toggleModalPostCampaign(true))
              }
            }}
            size="medium"
          >
            {t('Donate')}
          </CTA>
        </CTAWrapper>
      )}
    </SidebarSecondaryBlock>
  )
}

type SidebarRouterLinkProps = {
  to: To
} & LinkProps

export function SidebarRouterLink({
  to,
  ...props
}: SidebarRouterLinkProps): React.ReactElement<SidebarRouterLinkProps> {
  return (
    <Link as={RouterLink} tinted uppercase to={to}>
      {props.children}
    </Link>
  )
}
